/**
 * White-label branding provider (WS-3.5 / S13).
 *
 * A lightweight theming layer for the operator shell: it fetches the org's
 * effective branding from `GET /api/branding` and, on success, applies the
 * primary/accent colors to the `--brand-*` CSS custom properties and exposes
 * `display_name` + `logo_url` via context for the shell (brand name, logo).
 *
 * Inert + non-blocking by contract: the fetch never blocks render (children
 * always render), and on any error (e.g. a 403 for an external role, or the
 * endpoint being unavailable) it falls back to platform defaults — the CSS
 * variables in tokens.css remain in effect. This complements the boot-time
 * current-user brand-color apply in main.tsx; here the source of truth is the
 * dedicated /api/branding surface, so an admin's branding edits show up app-wide.
 */

import { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface Branding {
  display_name: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  terminology_overrides: Record<string, string>;
  ai_enabled: boolean;
}

// IGNITE's flame red and gold. These are written to <html> as an INLINE STYLE,
// which outranks tokens.css — so these two literals, not the stylesheet, decide
// what colour the app actually is. Keep them in step with the server-side
// defaults in src/services/branding.js.
const PLATFORM_DEFAULTS: Branding = {
  display_name: 'Ignite',
  logo_url: null,
  primary_color: '#C8102E',
  accent_color: '#D4A017',
  terminology_overrides: {},
  ai_enabled: false,
};

const BrandingContext = createContext<Branding>(PLATFORM_DEFAULTS);

/** Read the current org's effective branding from context. */
export function useBranding(): Branding {
  return useContext(BrandingContext);
}

/**
 * Resolve a single term through the org's terminology overrides, falling back
 * to the given default label. Lets a surface say `t('parcel', 'Parcel')`.
 */
export function useTerm(): (key: string, fallback: string) => string {
  const { terminology_overrides } = useBranding();
  return (key: string, fallback: string) => terminology_overrides[key] ?? fallback;
}

/** Apply primary/accent colors to the document root's --brand-* variables. */
function applyBrandingVars(b: Branding): void {
  const root = document.documentElement;
  if (b.primary_color) root.style.setProperty('--brand-primary', b.primary_color);
  if (b.accent_color) root.style.setProperty('--brand-accent', b.accent_color);
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery<Branding>({
    queryKey: ['branding'],
    queryFn: ({ signal }) => api.get<Branding>('/api/branding', signal),
    // Non-blocking + resilient: never retry (a 403 for external roles is expected),
    // and fall back to defaults so the shell always renders.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const branding = data ?? PLATFORM_DEFAULTS;

  useEffect(() => {
    applyBrandingVars(branding);
  }, [branding.primary_color, branding.accent_color]);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}
